<script setup lang="ts">
import { isEqual } from "lodash-es";
import { computed, ref, watch } from "vue";

import { getBoardEventLog } from "@/backend/read/BackendReader";
import SidePanelContent from "@/components/SidePanel/SidePanelContent.vue";
import ComplexList from "@/components/ui/ComplexList/ComplexList.vue";
import ListBanner from "@/components/ui/ComplexList/ListBanner.vue";
import ListEmpty from "@/components/ui/ComplexList/ListEmpty.vue";
import LoadingIndicator from "@/components/ui/LoadingIndicator.vue";
import type { ChangeTypeItem, StickyChange } from "@/model/change";
import { useActivityStore } from "@/store/activity";
import { useBoardStore } from "@/store/board";
import { fadeOutAllCardMarks, resetAllCardMarks, resetCardMark } from "@/store/helpers/mark";

import ChangeTypesFilter from "./internal/ChangeTypesFilter.vue";
import ChangesByDay from "./internal/ChangesByDay.vue";

const store = useActivityStore();
const changes = ref<StickyChange[]>([]);
const loading = ref(false);
const moreResults = ref<boolean>();

const changeTypes = computed({
  get: () => store?.selectedChangeTypes,
  set: (values: ChangeTypeItem[]) => store.selectChangeTypes(values),
});

watch(
  () => ({ ...store.allFilters }),
  async (newState, prevState) => {
    const board = useBoardStore().board;
    // somehow the watch triggers sometimes even if neither board nor date change
    // check it to avoid multiple calls to the backend
    const somethingChanged = !isEqual(newState, prevState);

    if (board && somethingChanged) {
      if (store.board && newState.date) {
        // Collect the iteration and type filters, if any
        const filters = useActivityStore().serverFilters;

        loading.value = true;
        const loadEvents = getBoardEventLog(
          board.id,
          newState.date.fromDate,
          newState.date.toDate,
          filters,
          [...newState.changeTypes, "!a_center", "!stack_pos"],
        );
        // wait until ToggleButton finishes transition; otherwise, animation
        // will freeze for a moment due to resources allocation for drawing
        // a substantial amount of list items
        const [ret] = await Promise.all([
          loadEvents,
          new Promise<void>((resolve) => setTimeout(() => resolve(), 300)),
        ]);
        changes.value = ret.changes;
        moreResults.value = ret.moreChanges;
        loading.value = false;
      } else {
        changes.value = [];
        store.resetFilters();
      }

      updateHighlightedCards();
    }
  },
  { immediate: true },
);

/**
 * Highlight the cards that match the current filters
 */
const updateHighlightedCards = () => {
  const board = useBoardStore().board;

  if (board && store.board && store.selectedDate && !loading.value) {
    fadeOutAllCardMarks(board);
    for (const change of changes.value) {
      resetCardMark(board.cards[change.stickyId]);
    }
  } else if (board) {
    resetAllCardMarks(board);
  }
};
</script>

<template>
  <SidePanelContent class="board-log-panel">
    <template #sub-title>
      <div class="panel-header">
        <h2 class="subtitle">{{ $t("boardDiff.log.title") }}</h2>
        <p class="description">{{ $t("boardDiff.log.description") }}</p>
        <ChangeTypesFilter v-model="changeTypes" />
      </div>
    </template>

    <ComplexList>
      <!-- Changes, grouped by day -->
      <ChangesByDay :changes show-cards />
      <!-- 'More results found - refine your search to see them' -->
      <ListBanner v-if="moreResults">{{ $t("boardDiff.log.moreResults") }}</ListBanner>

      <!-- Loading Spinner -->
      <ListEmpty v-if="loading">
        <LoadingIndicator />
      </ListEmpty>

      <!-- Empty List Message -->
      <ListEmpty v-if="changes.length === 0 && !loading">
        {{ $t("searchResults.noResults") }}
      </ListEmpty>
    </ComplexList>
  </SidePanelContent>
</template>
